import { ChangeEvent, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useConditionalEffect } from 'react-delta';
import classNames from 'classnames';
import PeopleIcon from '@mui/icons-material/People';

import { NO_TEAM_EMAILS_PARAM, PLACEMENT } from 'common/constants';
import { filterList } from 'common/util/array';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { teamEmailsState } from 'components/common/Managers/RouteStateManager.state';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import IconButton from 'components/common/IconButton/IconButton';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import TruncateWithTooltip, { TooltipPlacement } from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import Toolbar from 'components/common/Toolbar/Toolbar';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import Badge from 'components/common/Badge/Badge';
import { activeTeamState, availableNonactiveTeamsState, hasTeamsState, Team } from 'components/account/account.state';

import './CrossTeamSearch.scss';

interface CrossTeamSearchProps {
  className?: string;
}

const excludeActiveTeam = (activeTeam: Team) => (teamEmail: string) =>
  teamEmail !== activeTeam.emailAddress.toLowerCase();

/**
 * CrossTeamSearch is a component that renders a dropdown component that allows
 * a user to toggle on and off individual teams that they are linked to and updates
 * the 'teamEmails' url param which kicks off a search for cross team data.
 *
 * TODO: Set up test environments to have linked teams so we can write some basic E2E tests
 * {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5478}
 */
function CrossTeamSearch({ className }: CrossTeamSearchProps) {
  const isNewSearch = useIsNewSearch();
  const setSearchParams = useSetSearchParams();
  const [showOverlay, setShowOverlay] = useState(false);
  const [teamFilter, setTeamFilter] = useState('');
  const activeTeam = useRecoilValue(activeTeamState);
  const availableNonactiveTeams = useRecoilValue(availableNonactiveTeamsState);
  const hasTeams = useRecoilValue(hasTeamsState);
  const teamEmails = useRecoilValue(teamEmailsState) ?? [];
  const defaultTeamList = teamEmails.filter(excludeActiveTeam(activeTeam));
  const [teamEmailsDraft, setTeamEmailsDraft] = useState(defaultTeamList);
  const availableFilteredTeams = filterList(availableNonactiveTeams, teamFilter, 0, ({ displayName }) => [displayName]);
  const allSelected =
    availableFilteredTeams.length > 0 &&
    availableFilteredTeams.every((team) => teamEmailsDraft.includes(team.emailAddress.toLowerCase()));

  useConditionalEffect(() => {
    // If a new search gets issued from some other action in the application before this dropdown form is actually submitted,
    // reset our state to honor the URL as source of truth.
    setTeamEmailsDraft(teamEmails.filter(excludeActiveTeam(activeTeam)));
    setTeamFilter('');
  }, isNewSearch);

  const runSearch = () => {
    // If we have emails selected, be sure to include current active team in list.
    const teamEmailsNewParam =
      teamEmailsDraft.length > 0
        ? [activeTeam.emailAddress.toLowerCase()].concat(teamEmailsDraft).join(',')
        : NO_TEAM_EMAILS_PARAM;
    setSearchParams({ teamEmails: teamEmailsNewParam });
    setTeamFilter('');
    setShowOverlay(false);
  };

  const clearSearch = () => {
    setSearchParams({ teamEmails: '' });
    setTeamEmailsDraft([]);
    setTeamFilter('');
    setShowOverlay(false);
  };

  const onTeamEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    setTeamEmailsDraft(
      target.checked
        ? teamEmailsDraft.concat(target.value)
        : teamEmailsDraft.filter((field: string) => field !== target.value),
    );
  };

  const onAllTeamsSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    setTeamEmailsDraft(
      target.checked ? availableFilteredTeams.map(({ emailAddress }) => emailAddress.toLowerCase()) : [],
    );
  };

  const onFilterChange = (inputFilter: string) => {
    setTeamFilter(inputFilter);
    // Reset what teams have been selected, start fresh.
    setTeamEmailsDraft([]);
  };

  const currentTeamSection = (
    <div className="CrossTeamSearch__CurrentTeamSection">
      <CheckboxRadio
        type="checkbox"
        disabled
        label={
          <TruncateWithTooltip
            id={`TeamLabel-tooltip: ${activeTeam.id}`}
            text={activeTeam.displayName}
            ellipsisPlacement={PLACEMENT.END}
            maxLength={50}
          />
        }
        value={activeTeam.emailAddress.toLowerCase()}
        checked
      />
    </div>
  );

  let teamsList;
  if (hasTeams) {
    const allCheckbox = availableFilteredTeams.length > 0 && (
      <CheckboxRadio type="checkbox" label="All" value="All" checked={allSelected} onChange={onAllTeamsSelected} />
    );

    teamsList = (
      <>
        {allCheckbox}
        {availableFilteredTeams.map((team, idx: number) => {
          // If the team name matches the email address, then there will only be an overlay for a long display name
          if (team.displayName.toLowerCase() === team.emailAddress.toLowerCase()) {
            return (
              <div key={team.id}>
                <CheckboxRadio
                  type="checkbox"
                  label={
                    <TruncateWithTooltip
                      id={`TeamLabel-tooltip: ${team.id}-${idx}`}
                      text={team.displayName}
                      placement={TooltipPlacement.RIGHT}
                      ellipsisPlacement={PLACEMENT.END}
                      maxLength={50}
                    />
                  }
                  value={team.emailAddress.toLowerCase()}
                  checked={teamEmailsDraft.includes(team.emailAddress.toLowerCase())}
                  onChange={onTeamEmailChange}
                />
              </div>
            );
          }
          // If the team name does not match the email address, show the email address in a tooltip.
          // Also, replace TruncateWithTooltip with text-overflow: ellipsis so that there are not multiple tooltips
          return (
            <ElementWithTooltip key={team.id} placement={TooltipPlacement.RIGHT} tooltip={team.emailAddress}>
              <div className="CrossTeamSearch__SelectTeamsSection--CheckboxContainer">
                <CheckboxRadio
                  type="checkbox"
                  className="CrossTeamSearch__SelectTeamsSection--TeamLabelContainer"
                  label={team.displayName}
                  value={team.emailAddress.toLowerCase()}
                  checked={teamEmailsDraft.includes(team.emailAddress.toLowerCase())}
                  onChange={onTeamEmailChange}
                />
              </div>
            </ElementWithTooltip>
          );
        })}
      </>
    );
  } else {
    teamsList = <div>No additional teams to search.</div>;
  }

  const selectTeamsSection = (
    <div className="CrossTeamSearch__SelectTeamsSection">
      <div className="CrossTeamSearch__SectionHeader">ADDITIONAL TEAMS</div>
      <ClearableInput
        className="CrossTeamSearch__Input"
        placeholder="Filter teams"
        size="sm"
        value={teamFilter}
        onChange={onFilterChange}
      />
      {teamsList}
    </div>
  );

  const footerSection = [
    <Button variant={ButtonVariant.PRIMARY} onClick={runSearch}>
      Go
    </Button>,
    <Button variant={ButtonVariant.SECONDARY} onClick={clearSearch}>
      Clear
    </Button>,
  ];

  const dropdownBody = (
    <div className="Dropdown__Menu CrossTeamSearch__Dropdown">
      {currentTeamSection}
      {selectTeamsSection}
      <div className="CrossTeamSearch__Footer">
        <Toolbar leftItems={footerSection} />
      </div>
    </div>
  );

  const teamCountBadge = (
    <Badge className="CrossTeamSearch__CountBadge" variant="info">
      {teamEmails.length}
    </Badge>
  );

  let tooltipText = `Searching ${activeTeam.displayName}, click to search additional teams.`;
  if (teamEmails.length > 0) {
    tooltipText = `Currently searching across ${teamEmails.length} ${teamEmails.length > 1 ? 'teams' : 'team'}`;
  }

  return (
    <OverlayTrigger
      placement="bottom-start"
      overlay={dropdownBody}
      showOverlay={showOverlay}
      setShowOverlay={setShowOverlay}
    >
      <IconButton
        className="CrossTeamSearch__Button"
        containerClassName={classNames('CrossTeamSearch__Container', className)}
        tooltip={tooltipText}
        toolTipPlacement="right"
        showTooltip={!showOverlay}
        icon={<PeopleIcon />}
      >
        {teamEmails.length > 0 ? teamCountBadge : undefined}
      </IconButton>
    </OverlayTrigger>
  );
}

export default CrossTeamSearch;
